import { getCountdownPositionClass } from '@lib/countdownPosition';
import { buildGoogleFontStylesheetUrl, toCssFontFamily } from '@lib/googleFonts';
import { getTemplateBackgroundPath, getTemplateFontUrls, TimerTemplate } from '@lib/timerTemplates';
import Head from 'next/head';
import { CSSProperties, Fragment } from 'react';
import { CountdownPosition } from '@/types';

type TimerTemplatePreviewProps = {
	template: TimerTemplate;
	countdownPosition: CountdownPosition;
	/** A bar tile is wide and short, a product container tile is narrower and taller. */
	isWide: boolean;
};

const STATIC_TIME_UNITS = [
	{ value: '01', label: 'Days' },
	{ value: '11', label: 'Hours' },
	{ value: '59', label: 'Minutes' },
	{ value: '59', label: 'Seconds' },
];

/** A static miniature of a template, laid out with the classes of the live timer (`countdownTimer.css`). */
export default function TimerTemplatePreview({ template, countdownPosition, isWide }: TimerTemplatePreviewProps) {
	const { patch } = template;
	const backgroundPath = getTemplateBackgroundPath(template);
	const fontUrls = [buildGoogleFontStylesheetUrl(patch.font), ...getTemplateFontUrls(template.id)];

	return (
		<>
			<Head>
				{fontUrls.map((url) => (
					<link key={url} href={url} rel='stylesheet' />
				))}
			</Head>
			<div
				className={`timer-template-preview timer-template-preview-${template.id} ${
					isWide ? 'timer-template-preview-wide' : 'timer-template-preview-tall'
				} flex h-full w-full items-center justify-center overflow-hidden rounded-[22px] bg-cover bg-center px-12 py-8 max-md:px-9 max-md:py-4`}
				style={{
					backgroundColor: patch.background_color,
					backgroundImage: backgroundPath ? `url(/api${backgroundPath})` : undefined,
					color: patch.time_units_color,
					fontFamily: toCssFontFamily(patch.font),
				}}
			>
				<div className={`title-timer-container ${getCountdownPositionClass(countdownPosition)}`}>
					<div className='title-subtitle-container text-alignment-left'>
						<div className='title line-clamp-2' style={buildFontStyles(patch.title_font_style)}>
							{patch.title}
						</div>
						<div className='subtitle line-clamp-2' style={buildFontStyles(patch.subtitle_font_style)}>
							{patch.subtitle}
						</div>
					</div>
					<div className='timer-countdown'>
						{STATIC_TIME_UNITS.map((unit, index) => (
							<Fragment key={unit.label}>
								<div className='time-unit'>
									<div className='label'>{unit.label}</div>
									<div
										className='number'
										style={{ color: patch.countdown_color, background: patch.timer_window_color }}
									>
										{unit.value}
									</div>
								</div>
								{index < STATIC_TIME_UNITS.length - 1 && (
									<div className='colon' style={{ color: patch.time_units_color }}>
										:
									</div>
								)}
							</Fragment>
						))}
					</div>
				</div>
			</div>
		</>
	);
}

/** Only the weight, the style and the color: the tile scales the font sizes itself. */
function buildFontStyles(styleString: string): CSSProperties {
	const [, fontWeightStyle, color] = styleString.split(' ');

	return {
		color,
		fontWeight: fontWeightStyle?.includes('bold') ? 700 : 400,
		fontStyle: fontWeightStyle?.includes('italic') ? 'italic' : 'normal',
	};
}
